/**
 * `W4-T06` — a conversation, and the box to add to it. Shared by both sides of a job.
 *
 * The messages are rendered as **text**: React escapes them, and nothing here reaches for
 * `dangerouslySetInnerHTML`. A message is what a stranger typed, and it stays that.
 */
import { useEffect, useRef, type ReactElement } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, FieldError, invalidProps } from '@marketplace/ui';
import { MESSAGE_BODY_MAX, type JobThreadView, type MessageSide } from '@marketplace/contracts';
import { type MessageActionData } from './message-form.js';

interface ThreadProps {
  view: JobThreadView;
  locale: string;
  /** Which side is reading — their own messages are labelled *Tú*. */
  viewer: MessageSide;
  /** What the other side is called: the provider's name, or *Cliente*. */
  otherName: string;
  /** The client's thread id, posted with the form. The provider has one thread per job. */
  threadId?: string;
  /** Shown when there is nothing yet. */
  emptyText: string;
}

export function Thread({
  view,
  locale,
  viewer,
  otherName,
  threadId,
  emptyText,
}: ThreadProps): ReactElement {
  const { t } = useTranslation();

  const time = (iso: string): string =>
    new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });

  return (
    <div data-testid="thread">
      {view.hasEarlier ? <p className="mp-meta">{t('thread.earlier')}</p> : null}

      {view.messages.length === 0 ? (
        <p className="mp-meta" data-testid="thread-empty">
          {emptyText}
        </p>
      ) : (
        // A log: new entries are announced politely without stealing focus from the reply box.
        <ol className="mp-list" role="log" aria-label={t('thread.log')} data-testid="thread-log">
          {view.messages.map((message) => (
            <li key={message.id} data-testid={`message-${message.from}`}>
              <p className="mp-meta">
                <strong>{message.from === viewer ? t('thread.you') : otherName}</strong>
                {' · '}
                <time dateTime={message.createdAt}>{time(message.createdAt)}</time>
              </p>
              <p className="mp-message">{message.body}</p>
            </li>
          ))}
        </ol>
      )}

      {view.canWrite ? (
        <MessageForm threadId={threadId} />
      ) : (
        <p className="mp-meta" data-testid="thread-closed">
          {t('thread.closed')}
        </p>
      )}
    </div>
  );
}

function MessageForm({ threadId }: { threadId: string | undefined }): ReactElement {
  const { t } = useTranslation();
  // A fetcher, not a `<Form>`: the page already has an action result of its own (the quote form,
  // the decision), and a message must neither replace it nor navigate.
  const fetcher = useFetcher<MessageActionData>();
  const form = useRef<HTMLFormElement>(null);

  const error =
    fetcher.data !== undefined && 'messageError' in fetcher.data ? fetcher.data.messageError : null;

  // Cleared once the server has the message, never before: a failed send keeps what was typed.
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data !== undefined && 'sent' in fetcher.data) {
      form.current?.reset();
    }
  }, [fetcher.state, fetcher.data]);

  const fieldId = threadId === undefined ? 'message-body' : `message-body-${threadId}`;

  return (
    <fetcher.Form
      method="post"
      ref={form}
      className="mp-form mp-stack-form"
      data-testid="message-form"
      noValidate
    >
      <input type="hidden" name="intent" value="message" />
      {threadId === undefined ? null : <input type="hidden" name="threadId" value={threadId} />}

      <label htmlFor={fieldId}>{t('thread.form.label')}</label>
      <textarea
        id={fieldId}
        name="body"
        rows={3}
        maxLength={MESSAGE_BODY_MAX}
        {...invalidProps(error, `${fieldId}-error`)}
      />
      <FieldError id={`${fieldId}-error`} message={error === null ? undefined : t(error)} />

      <Button
        type="submit"
        variant="primary"
        isPending={fetcher.state !== 'idle'}
        pendingLabel={t('thread.form.pending')}
      >
        {t('thread.form.submit')}
      </Button>
    </fetcher.Form>
  );
}
